/*
 * Copyright (C) Zoomdata, Inc. 2012-2022. All rights reserved.
 */
import { EmbeddedComponent } from './EmbeddedComponent';
import { ComponentTypes, EditorPlacements, REFRESH_DATA, SettingTypes, } from './types';
import { convertToURLParameters, dispatchEmbedCustomEvent } from './utilities';
export var ApplyFiltersStrategy;
(function (ApplyFiltersStrategy) {
    ApplyFiltersStrategy["OVERRIDE_SAME_PATH"] = "overrideSamePath";
    ApplyFiltersStrategy["REPLACE_EXISTING"] = "replaceExisting";
})(ApplyFiltersStrategy || (ApplyFiltersStrategy = {}));
export class EmbeddedDashboard extends EmbeddedComponent {
    notificationSettings;
    header;
    dashboardId;
    originId;
    editor;
    route;
    interactivityProfileName;
    suppressAutoLayoutWarning;
    suppressResponsiveLayout;
    hideRestrictedVisuals;
    visualLegendType;
    componentType = ComponentTypes.DASHBOARD;
    interactivityOverrides;
    menuEventsConfig;
    editorUserSettings;
    initialFilters;
    availableVisTypes;
    /**
     *
     * @param theme - The theme for the embedded dashboard. Valid values are `composer`, `modern`, or `dark`.
     * The initial default theme, `composer`, is the same as the `modern` theme.
     * However, if you add your own themes to the application, more options are available in this list and you may have introduced a different default.
     * @param application - `application.banner` Indicates whether the Composer top-level navigation banner should be shown with the embedded dashboard.
     * Valid values are `true` or `false`.
     *
     * `application.logo` Indicates whether the Composer logo should be shown with the embedded dashboard. Valid values are `true` or `false`.
     * @param renderer
     * @param dashboardId - The dashboard ID. If no dashboard ID is specified, an empty dashboard is embedded.
     *
     * @param originId - The originId. `originId` takes precedence over `dashboardId`. Only one of `originId` or `dashboardId` should be provided.
     *
     * @param header - `header.showActions` Indicates whether dashboard actions should be visible for the embedded dashboard. Valid values are `true` or `false`.
     *
     * `header.showTitle` Indicates whether the dashboard title should be shown for the embedded dashboard. Valid values are `true` or `false`.
     *
     * `header.title` Allows you to overwrite the title of the embedded dashboard. The embedded dashboard title is read only; it cannot be changed while the dashboard is embedded.
     *
     * `header.dashboardSettingsPlacement` Indicates how interactivity, dashboard filters and layout settings panels should looks like. Valid settings placements are `singlePanel` and `multiplePanels`. Applies when RESPONSIVE_LAYOUT toggle is on.
     *
     * header.visible Indicates whether the dashboard header should be shown for the embedded dashboard. Valid values are `true` or `false`.
     * @param suppressAutoLayoutWarning - Allows you to suppress visibility of convert layout banner. Valid values are `true` of `false`.
     * @param suppressResponsiveLayout - Allows you to suppress applying adaptive layout. Valid values are `true` of `false`.
     * @param hideRestrictedVisuals - Allows you to hide visuals that users do not have access to. Works with new dashboard layout.
     * @param editor - `editor.placement` Indicates where the dashboard editor appears. Valid editor placements are `dockRight` and `modals`.
     * @param interactivityProfileName - The name of the dashboard interactivity profile that should be used for the embedded dashboard.
     * @param interactivityOverrides - Specifies specific dashboard and visual interactivity settings for an embedded dashboard.
     * The visual interactivity settings specified in this object will override any interactivity settings specified for the individual
     * visuals. These settings will also override the dashboard interactivity profile linked to the dashboard
     * (the one saved with the dashboard) and the dashboard interactivity profile passed by the `interactivityProfileName` property.
     * @param notificationSettings - Specifies settings for notifications
     * @param menuEventsConfig - Specifies event setup for context visual menu
     * @param editorUserSettings - If `editorUserSettings.isViewMode` is true, then View mode is applied, which restricts some editing abilities (layout change, adding visuals, save, etc.). Default value is `false`.
     *
     * `editorUserSettings.canSwitchMode` Indicates whether the editor can switch between edit and view modes. Valid values are `true` or `false`. Default value is `true`.
     *
     * `editorUserSettings.allowInteractivityConfiguration` Indicates whether the editor can edit interactivity profile. Valid values are `true` or `false`. Default value is `false`.
     *
     * @param visualLegendType - Specifies Visual Legend type for embedded dashboard
     * In case of `staticLegend` - composer legend will be used.
     * In case of `dynamicLegend - Echarts legend will be used.
     * In other cases - legend type will be taken from feature toggle value
     * @param initialFilters - EXPERIMENTAL. Allows you to pass initial filters for a single source. Passing sourceId is required.
     * Time Filters and Filter could be passed together or separately and will be applied to the visuals that are using passed source.
     * @param availableVisTypes - Specifies an array of limited visual types for embedded lite dashboard. While adding a new visual in embedded *lite-dashboard* component, if the selected source does not support `RAW_DATA_TABLE`,
     * then these limited visual types will be displayed for opting. Also the available visual types in the *Visual Style Panel* will be limited to these visual types.
     *
     *  For embedded dashboard this is `undefined`.
     *
     * For embedded *lite-dashboard*, the default available visual types are `['UBER_BARS','MULTI_METRIC_BARS','DONUT','KPI','BUBBLES','PIE','RAW_DATA_TABLE']`.
     */
    constructor(theme, application, renderer, dashboardId, originId, header, editor, interactivityProfileName, interactivityOverrides, notificationSettings, menuEventsConfig, suppressAutoLayoutWarning, suppressResponsiveLayout, hideRestrictedVisuals, editorUserSettings, visualLegendType, initialFilters, availableVisTypes) {
        super(theme, application, renderer, notificationSettings);
        this.notificationSettings = notificationSettings;
        if (dashboardId && originId) {
            console.warn('Both dashboardId and originId provided. Please use only one of them.');
        }
        this.dashboardId = dashboardId;
        this.editor = editor || {
            placement: EditorPlacements.MODALS,
        };
        this.availableVisTypes = availableVisTypes ?? undefined;
        this.suppressAutoLayoutWarning = suppressAutoLayoutWarning;
        this.suppressResponsiveLayout = suppressResponsiveLayout;
        this.hideRestrictedVisuals = hideRestrictedVisuals;
        this.visualLegendType = visualLegendType;
        this.header = {
            visible: true,
            showTitle: true,
            showActions: true,
            ...header,
        };
        let queryString = '';
        if (initialFilters && initialFilters.sourceId && (initialFilters.timeFilter || initialFilters.filters)) {
            // building a url params from filters
            const queryParams = {
                inheritSourceId: initialFilters.sourceId,
                from: initialFilters.timeFilter?.from,
                to: initialFilters.timeFilter?.to,
                timeField: initialFilters.timeFilter?.timeField,
                filters: JSON.stringify(initialFilters.filters),
            };
            const sanitizedQueryParams = Object.entries(queryParams).reduce((params, [key, value]) => {
                const valueStr = `${value}`;
                if (value !== undefined && valueStr) {
                    params[key] = valueStr;
                }
                return params;
            }, {});
            queryString = convertToURLParameters(sanitizedQueryParams);
        }
        if (dashboardId === null) {
            this.route = `visualization/create/${this.componentInstanceId}`;
        }
        else {
            this.route = `visualization/${dashboardId}/${this.componentInstanceId}${queryString && `?${queryString}`}`;
        }
        this.interactivityProfileName = interactivityProfileName;
        this.interactivityOverrides = interactivityOverrides;
        this.menuEventsConfig = menuEventsConfig;
        this.initialFilters = initialFilters;
        this.editorUserSettings = {
            isViewMode: false,
            canSwitchMode: true,
            allowInteractivityConfiguration: false,
            ...editorUserSettings,
        };
    }
    getApplicationParams() {
        const base = super.getApplicationParams();
        return {
            ...base,
            editor: this.editor,
            header: this.header,
            suppressAutoLayoutWarning: this.suppressAutoLayoutWarning,
            suppressResponsiveLayout: this.suppressResponsiveLayout,
            hideRestrictedVisuals: this.hideRestrictedVisuals,
            mode: 'interactive',
            route: this.route,
            interactivityProfileName: this.interactivityProfileName,
            interactivityOverrides: this.interactivityOverrides,
            menuEventsConfig: this.menuEventsConfig,
            editorUserSettings: this.editorUserSettings,
            visualLegendType: this.visualLegendType,
            initialFilters: this.initialFilters,
            availableVisTypes: this.availableVisTypes,
        };
    }
    render(element, attributes) {
        super.render(element, attributes);
        this.htmlElement.addEventListener('composer-dashboard-loaded', this.removeLoader);
    }
    refreshData() {
        dispatchEmbedCustomEvent(REFRESH_DATA, { options: { componentInstanceId: this.componentInstanceId } });
    }
    destroy() {
        this.htmlElement.removeEventListener('composer-dashboard-loaded', this.removeLoader);
    }
    addEventListener(eventName, callback) {
        this.htmlElement.addEventListener(eventName, callback);
    }
    removeEventListener(eventName, callback) {
        this.htmlElement.removeEventListener(eventName, callback);
    }
}
//# sourceMappingURL=EmbeddedDashboard.js.map